import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
  FiUserCheck,
} from "react-icons/fi";
import PayoutBeneficiaryFlowSteps from "./PayoutBeneficiaryFlowSteps";
import { ensurePayoutsAccessToken, payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  isPayoutStepUpEnabled,
  pickString,
  readSessionJson,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { validateOtpCode } from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

const defaultBeneficiaryDraft = {
  payoutAccount: { id: "", label: "", meta: "" },
  beneficiaryName: "",
  category: { label: "", value: "" },
  mobileNumber: "",
  email: "",
  accountNumber: "",
  ifscCode: "",
  accountType: { label: "", value: "" },
  branchName: "",
  remarks: "",
  tags: "",
};

const displayText = (value?: string) => value?.trim() || "-";

const maskedAccount = (accountNumber?: string) => {
  const last4 = accountNumber?.replace(/\D/g, "").slice(-4) || "";
  return last4 ? `**** ${last4}` : "-";
};

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

function PayoutAddBeneficiaryVerify() {
  const navigate = useNavigate();
  const beneficiaryDraft = readSessionJson(
    "payoutBeneficiaryDraft",
    defaultBeneficiaryDraft,
  );
  const method = "authenticator" as const;
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const stepUpRequired = isPayoutStepUpEnabled();
  const ready = !validateOtpCode(code);
  const verificationInstruction = stepUpRequired
    ? "Enter the 6-digit code from your Google Authenticator app."
    : "Verify beneficiary: enter the 6-digit verification code to continue.";

  const updateDigit = (index: number, value: string) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      const nextOtp = [...otp];
      nextOtp[index] = "";
      setOtp(nextOtp);
      return;
    }

    // Support typing/pasting multiple digits into one box.
    if (digits.length > 1) {
      handlePaste(digits);
      return;
    }

    const nextOtp = [...otp];
    nextOtp[index] = digits;
    setOtp(nextOtp);

    if (index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleContinue = async () => {
    setSubmitError("");
    const otpError = validateOtpCode(
      code,
      "Verify beneficiary: enter the 6-digit Google Authenticator code to create this beneficiary.",
    );
    if (otpError) {
      showSubmitError(otpError);
      return;
    }
    if (submitting) return;

    // Local / UAT: OTP is UI-only for now (STEP_UP disabled, no SMS).
    // Skip step-up + bank name verify; create row then go to confirm.
    if (isPayoutMockMode()) {
      showSubmitError(
        "Mock mode is active — beneficiary is not saved to the API. Set REACT_APP_PAYOUTS_DATA_SOURCE=api and restart npm start.",
      );
      return;
    }

    setSubmitting(true);
    try {
      await ensurePayoutsAccessToken();
      const payoutAccountId = pickString(
        beneficiaryDraft.payoutAccount,
        ["funding_account_id", "fundingAccountId", "id", "value", "account_id"],
      );
      if (!payoutAccountId || payoutAccountId === "operating-account") {
        throw new Error(
          "Missing payout bank account. Go back and select Bharat UAT Current (fa_bharat_ops).",
        );
      }
      const body = compactBody({
        funding_account_id: payoutAccountId,
        category: pickString(beneficiaryDraft.category, ["value", "label"], "vendor"),
        name_entered: beneficiaryDraft.beneficiaryName,
        name: beneficiaryDraft.beneficiaryName,
        mobile: beneficiaryDraft.mobileNumber,
        email: beneficiaryDraft.email,
        account_number: beneficiaryDraft.accountNumber,
        ifsc: beneficiaryDraft.ifscCode,
        account_type: pickString(beneficiaryDraft.accountType, ["label", "value"], "Savings"),
        branch_name: beneficiaryDraft.branchName,
        remarks: beneficiaryDraft.remarks,
        tags: beneficiaryDraft.tags
          ? beneficiaryDraft.tags
              .split(",")
              .map((tag: string) => tag.trim())
              .filter(Boolean)
          : undefined,
      });
      const result = await payoutApi.createBeneficiary(
        body,
        stableIdempotencyKey(
          "create-beneficiary",
          `${body.account_number}-${body.ifsc}`,
        ),
      );
      const beneficiaryId = pickString(result, [
        "beneficiary_id",
        "beneficiaryId",
        "id",
      ]);
      let verified = result;
      if (beneficiaryId) {
        const stepUpToken = stepUpRequired
          ? await verifyStepUp({
              purpose: "VERIFY_BENEFICIARY",
              method,
              code,
              subjectId: beneficiaryId,
            })
          : undefined;
        verified = await payoutApi.verifyBeneficiary(
          beneficiaryId,
          {},
          stableIdempotencyKey("verify-beneficiary", beneficiaryId),
          stepUpToken,
        );
      }
      writeSessionJson("payoutBeneficiaryCreateResult", {
        ...verified,
        skipped_verification: false,
      });
      const verificationStatus = pickString(
        verified,
        ["verification_status", "verificationStatus"],
        "",
      ).toUpperCase();
      if (verificationStatus === "MISMATCH_BLOCKED") {
        throw new Error(
          "Account name does not match bank records. Beneficiary was blocked.",
        );
      }
      if (verificationStatus === "FAILED") {
        throw new Error(
          "Name verification failed. You can retry from Beneficiaries.",
        );
      }
      navigate("/payouts/beneficiaries/add/confirm");
    } catch (error) {
      showSubmitError(errorMessage(error, "Verify beneficiary: unable to create this beneficiary. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify Beneficiary
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify this beneficiary before it becomes available for payouts.
        </p>
      </div>

      <PayoutBeneficiaryFlowSteps activeStep={3} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiUserCheck />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Beneficiary Details
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {verificationInstruction}
              </p>
            </div>
          </div>

          <div>
            <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Use your linked authenticator app
                    </span>
                  </span>
                </span>
                <FiCheck className="text-lg text-[#16A34A]" />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && !otp[index] && index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }}
                  inputMode="numeric"
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setOtp(["", "", "", "", "", ""])}
                className="flex items-center gap-2 text-[#1B163B]"
              >
                <FiRefreshCw />
                Resend code
              </button>
            </div>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Verification Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow
            label="Beneficiary"
            value={displayText(beneficiaryDraft.beneficiaryName)}
          />
          <SummaryRow
            label="Payout Account"
            value={displayText(pickString(beneficiaryDraft.payoutAccount, ["label", "name"]))}
          />
          <SummaryRow label="Bank" value="-" />
          <SummaryRow
            label="Account Number"
            value={maskedAccount(beneficiaryDraft.accountNumber)}
          />
          <SummaryRow label="Verification Method" value="Google Authenticator" />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            Your beneficiary details are locked while verification is in
            progress.
          </div>
        </aside>
      </div>

      <div className="mt-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-4 new-shadow">
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/payouts/beneficiaries/add/review"
            className="flex h-11 min-w-[140px] items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            <FiArrowLeft />
            Back
          </Link>
          <button
            type="button"
            onClick={() => void handleContinue()}
            disabled={!ready || submitting}
            style={{
              backgroundColor: ready && !submitting ? "#1B163B" : "#A3AED0",
              color: "#FFFFFF",
            }}
            className={
              "ml-auto flex h-11 min-w-[200px] items-center justify-center gap-3 rounded-[6px] px-8 text-sm font-semibold text-white shadow-sm " +
              (ready && !submitting ? "hover:brightness-95" : "cursor-not-allowed")
            }
          >
            {submitting ? "Continuing..." : "Continue"}
            <FiArrowRight className="text-lg" />
          </button>
        </div>
        {!ready && stepUpRequired && (
          <p className="mt-2 text-right text-xs font-semibold text-[#EF4444]">
            Enter all 6 digits to enable Continue
          </p>
        )}
      </div>
    </div>
  );
}

export default PayoutAddBeneficiaryVerify;

